import { useEffect, useState, useRef } from 'react';
import { Button, Space, Typography, Spin, message, Popconfirm, Tooltip } from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { api } from '../api';

const { Text } = Typography;

const IMAGE_TYPES = [
  { key: 'logo',    label: 'Logo',        hint: 'Wird auf der Store-Kachel im Shop angezeigt' },
  { key: 'image',   label: 'Header-Bild', hint: 'Großes Titelbild der Store-Kachel' },
  { key: 'receipt', label: 'Bon-Header',  hint: 'Kopfbild auf dem Bestellbon' },
];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // result is "data:image/png;base64,XXXXX" — strip the prefix
      resolve(reader.result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ImageSlot({ storeId, type, label, hint, image, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const inputRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = await fileToBase64(file);
      await api.images.upload(storeId, type, { filename: file.name, mimeType: file.type, data });
      message.success(`${label} hochgeladen`);
      onChanged();
    } catch (err) {
      message.error(err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.images.delete(storeId, type);
      message.success(`${label} gelöscht`);
      onChanged();
    } catch (err) {
      message.error(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const previewUrl = image ? `/api/public/images/${image.id}` : null;

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ marginBottom: 6 }}>
        <Text strong>{label}</Text>
        <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>{hint}</Text>
      </div>

      {previewUrl ? (
        <div style={{ marginBottom: 10 }}>
          <img
            src={previewUrl}
            alt={label}
            style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 6, border: '1px solid #e8d5b0', display: 'block' }}
          />
          <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
            {image.filename}
          </Text>
        </div>
      ) : (
        <div style={{
          height: 80, marginBottom: 10,
          background: '#f5ede0', borderRadius: 6,
          border: '1px dashed #e8d5b0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Text type="secondary">Kein Bild</Text>
        </div>
      )}

      <Space>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFile}
        />
        <Button
          icon={<UploadOutlined />}
          size="small"
          loading={uploading}
          onClick={() => inputRef.current.click()}
        >
          {previewUrl ? 'Ersetzen' : 'Hochladen'}
        </Button>
        {previewUrl && (
          <Popconfirm
            title={`${label} löschen?`}
            onConfirm={handleDelete}
            okText="Löschen"
            cancelText="Abbrechen"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title={`${label} entfernen`}>
              <Button icon={<DeleteOutlined />} size="small" danger loading={deleting} />
            </Tooltip>
          </Popconfirm>
        )}
      </Space>
    </div>
  );
}

export default function StoreImagesDrawer({ storeId, storeName, onChanged }) {
  const [images, setImages]   = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const rows = await api.images.list(storeId);
      setImages(rows);
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [storeId]);

  const imageByType = (type) => images.find(i => i.type === type) || null;

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;

  return (
    <div>
      {IMAGE_TYPES.map(({ key, label, hint }) => (
        <ImageSlot
          key={key}
          storeId={storeId}
          type={key}
          label={label}
          hint={hint}
          image={imageByType(key)}
          onChanged={() => { load(); onChanged?.(); }}
        />
      ))}
    </div>
  );
}
